import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import QRCode from 'react-qr-code';
import { trackPageView, trackVideoPlay, trackPhotoView } from '../utils/analytics';
import { 
  Building2, MapPin, Calendar, Globe, Handshake, Award, User, Mail, Phone,
  Link as LinkIcon, Play, Share2, Copy, Check, ExternalLink,
  MessageCircle, Linkedin, Loader2, AlertCircle, Video, ChevronRight, 
  Briefcase, Star, Edit, Users, Target, TrendingUp, Package, X,
  Rocket, Flag, DollarSign, CalendarDays, Users2, Megaphone, Sparkles, FolderOpen,
  Link2, Send, Download, QrCode, Eye, Tv, ArrowRight, Instagram, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png";

// Category configuration with colors and icons
const CATEGORY_CONFIG = {
  general: { 
    label: 'Company Profile', 
    color: 'zinc', 
    bgClass: 'bg-zinc-700/20 border-zinc-600/30 text-zinc-300',
    icon: Building2,
    cta: 'Get in Touch'
  },
  business_expansion: { 
    label: 'Business Expansion', 
    color: 'blue',
    bgClass: 'bg-blue-500/20 border-blue-500/30 text-blue-300',
    icon: Rocket,
    cta: 'Explore Expansion'
  },
  international: { 
    label: 'International Market', 
    color: 'teal',
    bgClass: 'bg-teal-500/20 border-teal-500/30 text-teal-300',
    icon: Globe,
    cta: 'Discuss Global Entry'
  },
  investment: { 
    label: 'Seeking Investment', 
    color: 'amber',
    bgClass: 'bg-amber-500/20 border-amber-500/30 text-amber-300',
    icon: DollarSign,
    cta: 'Schedule Investor Call'
  },
  trade_show: { 
    label: 'Trade Show', 
    color: 'rose',
    bgClass: 'bg-rose-500/20 border-rose-500/30 text-rose-300',
    icon: CalendarDays,
    cta: 'Visit Our Booth'
  },
  partnership: { 
    label: 'Partnership Seeking', 
    color: 'purple',
    bgClass: 'bg-purple-500/20 border-purple-500/30 text-purple-300',
    icon: Users2,
    cta: 'Explore Partnership'
  },
  announcement: { 
    label: 'Announcement', 
    color: 'green',
    bgClass: 'bg-green-500/20 border-green-500/30 text-green-300',
    icon: Megaphone,
    cta: 'Learn More'
  },
  other: { 
    label: 'Profile', 
    color: 'zinc',
    bgClass: 'bg-zinc-700/20 border-zinc-600/30 text-zinc-300',
    icon: Sparkles,
    cta: 'Get in Touch'
  }
};

// Extract YouTube video ID from URL
function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
}

// Bento Video Card Component
function BentoVideoCard({ video, size = 'default', onPlay }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const youtubeId = getYouTubeId(video?.url);
  
  if (!video?.url) return null;
  
  // Use BrandStage branded cover for hero, secondary cover for others
  const coverImage = size === 'hero' 
    ? 'https://static.prod-images.emergentagent.com/jobs/3b3b930a-8c3c-49e6-8785-22b39d87d257/images/79a289bb58fdd30bd15d2212b9679c188fff3b305e28a4cf8ffac29a8b2a30c0.jpeg'
    : '/images/secondary-video-cover.jpg';

  const handlePlay = () => {
    setIsPlaying(true);
    if (onPlay) onPlay();
  };
  
  return (
    <div className={`relative group rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-zinc-600 transition-all duration-300 aspect-video`}>
      {!isPlaying ? (
        <div 
          className="relative w-full h-full cursor-pointer"
          onClick={handlePlay}
          data-testid="video-card-thumbnail"
        >
          {/* BrandStage Branded Cover Image */}
          <img 
            src={coverImage}
            alt={video.title || 'Video'}
            className="w-full h-full object-cover"
          />
          
          {/* PROFILE label for hero video */}
          {size === 'hero' && (
            <div className="absolute top-4 left-4">
              <span className="text-white text-2xl md:text-3xl font-bold tracking-wide drop-shadow-lg" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
                PROFILE
              </span>
            </div>
          )}
          
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-[#C41E3A]/90 rounded-full flex items-center justify-center group-hover:bg-[#C41E3A] group-hover:scale-110 transition-all duration-300 shadow-lg">
              <Play className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" fill="white" />
            </div>
          </div>
          
          {/* Title overlay at bottom */}
          {video.title && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
              <p className="text-white font-medium text-sm line-clamp-2">{video.title}</p>
            </div>
          )}
        </div>
      ) : (
        youtubeId ? (
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?rel=0&autoplay=1`}
            title={video.title || 'Video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-zinc-800">
            <a 
              href={video.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 text-zinc-400 hover:text-white transition-colors"
            >
              <Play className="w-12 h-12" />
              <span className="text-sm">Open Video</span>
            </a>
          </div>
        )
      )}
    </div>
  );
}

// Testimonial Video Card Component
function TestimonialCard({ testimonial, onPlay }) {
  const [isPlaying, setIsPlaying] = useState(false);
  // Prefer branded video if available, fall back to original
  const videoSrc = testimonial?.branded_video_url || testimonial?.video_url;
  const youtubeId = getYouTubeId(testimonial?.video_url);

  const handlePlay = () => {
    setIsPlaying(true);
    if (onPlay) onPlay();
  };

  return (
    <div 
      className="relative group rounded-xl overflow-hidden bg-zinc-900/50 border border-zinc-800 hover:border-[#C41E3A]/50 transition-all duration-300"
      data-testid="testimonial-card"
    >
      {/* Video Area */}
      <div className="aspect-[4/3] relative">
        {!isPlaying ? (
          <div 
            className="w-full h-full cursor-pointer relative"
            onClick={handlePlay}
          >
            {/* Thumbnail */}
            {youtubeId ? (
              <img 
                src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
                alt={testimonial.contact_name}
                className="w-full h-full object-cover"
              />
            ) : videoSrc ? (
              <video 
                src={videoSrc}
                className="w-full h-full object-cover"
                muted
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                <User className="w-16 h-16 text-zinc-600" />
              </div>
            )}
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            
            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 group-hover:bg-[#C41E3A] group-hover:border-[#C41E3A] transition-all duration-300">
                <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
              </div>
            </div>
          </div>
        ) : (
          youtubeId ? (
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}?rel=0&autoplay=1`}
              title={testimonial.contact_name}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          ) : (
            <video 
              src={videoSrc}
              className="w-full h-full object-cover"
              controls
              autoPlay
            />
          )
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <p className="text-white font-semibold">{testimonial.contact_name}</p>
        <p className="text-zinc-400 text-sm">{testimonial.contact_company || 'Client'}</p>
        {testimonial.message && (
          <p className="text-zinc-500 text-xs mt-2 line-clamp-2 italic">&quot;{testimonial.message}&quot;</p>
        )}
      </div>
    </div>
  );
}

// KPI Stat Card Component
function KPICard({ value, label, icon: Icon, spanClass = "" }) {
  if (!value) return null;
  
  return (
    <div className={`bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center hover:border-zinc-700 transition-colors ${spanClass}`} data-testid="kpi-card">
      <div className="flex items-center justify-center mb-2">
        <Icon className="w-5 h-5 text-[#C41E3A]" />
      </div>
      <div className="text-2xl md:text-3xl font-bold text-white">{value}</div>
      <div className="text-xs text-zinc-500 uppercase tracking-wide mt-1">{label}</div>
    </div>
  );
}

// Smart KPI Row - adapts grid based on how many KPIs exist
function KPIRow({ kpis }) {
  const validKPIs = kpis.filter(k => k.value);
  if (validKPIs.length === 0) return null;
  
  // Determine grid columns based on count
  const gridClass = validKPIs.length === 1 
    ? 'grid-cols-1 max-w-xs mx-auto' 
    : validKPIs.length === 2 
    ? 'grid-cols-2 max-w-md mx-auto'
    : validKPIs.length === 3
    ? 'grid-cols-3'
    : 'grid-cols-2 md:grid-cols-4';
  
  return (
    <div className={`col-span-12 grid ${gridClass} gap-3`}>
      {validKPIs.map((kpi, idx) => (
        <KPICard key={idx} value={kpi.value} label={kpi.label} icon={kpi.icon} />
      ))}
    </div>
  );
}

// Category Badge Component
function CategoryBadge({ category, customCategory }) {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.general;
  const Icon = config.icon;
  const displayLabel = category === 'other' && customCategory ? customCategory : config.label;
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${config.bgClass}`} data-testid="category-badge">
      <Icon className="w-3.5 h-3.5" />
      {displayLabel}
    </div>
  );
}

// Video Modal Component
function VideoModal({ video, onClose }) {
  const youtubeId = getYouTubeId(video?.url);
  
  if (!video) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button 
        className="absolute top-4 right-4 text-white hover:text-zinc-300 transition-colors"
        onClick={onClose}
      >
        <X className="w-8 h-8" />
      </button>
      <div 
        className="w-full max-w-4xl aspect-video"
        onClick={e => e.stopPropagation()}
      >
        {youtubeId ? (
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?rel=0&autoplay=1`}
            title={video.title || 'Video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full rounded-xl"
          />
        ) : (
          <video 
            src={video.url}
            controls
            autoPlay
            className="w-full h-full rounded-xl"
          />
        )}
      </div>
    </div>
  );
}

export default function PublicProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [showcaseOpps, setShowcaseOpps] = useState([]);
  const [showcaseEvents, setShowcaseEvents] = useState([]);
  const [showcaseEnabled, setShowcaseEnabled] = useState(true);
  const [showcaseTitle, setShowcaseTitle] = useState('BrandStage Showcase');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const qrRef = useRef(null);
  
  // Multi-language translation state
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [translations, setTranslations] = useState({});
  const [translating, setTranslating] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  
  // Supported languages
  const LANGUAGES = [
    { code: 'en', name: 'English', native: 'English', flag: '🇬🇧' },
    { code: 'vi', name: 'Vietnamese', native: 'Tiếng Việt', flag: '🇻🇳' },
    { code: 'th', name: 'Thai', native: 'ไทย', flag: '🇹🇭' },
    { code: 'zh-CN', name: 'Chinese', native: '中文', flag: '🇨🇳' },
    { code: 'ja', name: 'Japanese', native: '日本語', flag: '🇯🇵' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी', flag: '🇮🇳' },
    { code: 'ar', name: 'Arabic', native: 'العربية', flag: '🇦🇪' },
    { code: 'id', name: 'Indonesian', native: 'Bahasa Indonesia', flag: '🇮🇩' },
    { code: 'ms', name: 'Malay', native: 'Bahasa Melayu', flag: '🇲🇾' },
    { code: 'ko', name: 'Korean', native: '한국어', flag: '🇰🇷' },
    { code: 'de', name: 'German', native: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'French', native: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Spanish', native: 'Español', flag: '🇪🇸' },
  ];
  
  // BrandStage Promo Bar state
  const [showPromoBar, setShowPromoBar] = useState(() => {
    // Check localStorage if user dismissed it
    return localStorage.getItem('brandstage_promo_dismissed') !== 'true';
  });
  const [promoBarVisible, setPromoBarVisible] = useState(true);
  const lastScrollY = useRef(0);
  
  // Scroll detection for mobile - show on scroll up, hide on scroll down
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        // Show on scroll up, hide on scroll down
        if (currentScrollY < lastScrollY.current - 10) {
          setPromoBarVisible(true);
        } else if (currentScrollY > lastScrollY.current + 10) {
          setPromoBarVisible(false);
        }
      } else {
        // Always visible on desktop
        setPromoBarVisible(true);
      }
      
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const dismissPromoBar = () => {
    setShowPromoBar(false);
    localStorage.setItem('brandstage_promo_dismissed', 'true');
  };
  
  // Translate profile content
  const translateProfile = async (langCode) => {
    if (langCode === 'en') {
      setSelectedLanguage('en');
      setTranslations({});
      return;
    }
    
    if (!profile?.id) return;
    
    setTranslating(true);
    try {
      const response = await axios.post(`${API_URL}/api/profiles/${profile.id}/translate`, {
        profile_id: profile.id,
        target_language: langCode
      });
      setTranslations(response.data.translations || {});
      setSelectedLanguage(langCode);
    } catch (err) {
      console.error('Translation failed:', err);
      // Show error but don't change language
      if (err.response?.status === 403) {
        alert('Translation is not enabled for this profile');
      }
    } finally {
      setTranslating(false);
      setShowLanguageMenu(false);
    }
  };
  
  // Helper to get translated text or original
  const t = (field, originalText) => {
    if (selectedLanguage === 'en' || !translations[field]) {
      return originalText;
    }
    return translations[field];
  };
  
  // Helper for profile sections
  const tSection = (sectionKey, originalText) => {
    if (selectedLanguage === 'en' || !translations.profile_sections?.[sectionKey]) {
      return originalText;
    }
    return translations.profile_sections[sectionKey];
  };
  
  // Helper for key_contact fields (handles both old and new translation formats)
  const tKeyContact = (fieldKey, originalText) => {
    if (selectedLanguage === 'en') {
      return originalText;
    }
    // New format: translations.key_contact.bio_text
    if (translations.key_contact?.[fieldKey]) {
      return translations.key_contact[fieldKey];
    }
    // Old format: translations.key_contact_bio (for backwards compatibility)
    if (fieldKey === 'bio_text' && translations.key_contact_bio) {
      return translations.key_contact_bio;
    }
    return originalText;
  };
  
  useEffect(() => {
    fetchProfile();
  }, [slug]);

  // Track profile page view
  useEffect(() => {
    if (slug && profile) {
      trackPageView(slug);
    }
  }, [slug, profile]);
  
  useEffect(() => {
    const checkEditPermission = async () => {
      if (!user || !profile) {
        setCanEdit(false);
        return;
      }
      
      const isAdmin = user.email?.toLowerCase() === 'brandstagevideo@gmail.com';
      if (isAdmin) {
        setCanEdit(true);
        return;
      }
      
      try {
        const response = await axios.get(`${API_URL}/api/profiles/mine`, { withCredentials: true });
        const userProfiles = response.data.profiles || [];
        const ownsProfile = userProfiles.some(p => p.slug === slug);
        setCanEdit(ownsProfile);
      } catch (err) {
        setCanEdit(false);
      }
    };
    
    checkEditPermission();
  }, [user, profile, slug]);
  
  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/p/${slug}`, { withCredentials: true });
      setProfile(response.data);
      
      // Track page view
      if (response.data.id) {
        axios.post(`${API_URL}/api/profiles/${response.data.id}/track?event_type=view`).catch(() => {});
      }
      
      // Fetch testimonials
      if (response.data.id) {
        try {
          const testimonialsRes = await axios.get(`${API_URL}/api/profiles/${response.data.id}/testimonials`);
          setTestimonials(testimonialsRes.data.testimonials || []);
        } catch (e) {
          console.log('No testimonials found');
        }
      }
      
      // Fetch opportunities linked to this profile
      try {
        const oppsRes = await axios.get(`${API_URL}/api/connect/profile/company/${slug}/opportunities`);
        if (oppsRes.data.show_on_profile) {
          setOpportunities(oppsRes.data.opportunities || []);
        }
      } catch (e) {
        console.log('No opportunities found');
      }

      // Fetch showcase opportunity pages
      try {
        const soRes = await axios.get(`${API_URL}/api/p/${slug}/opportunities`);
        setShowcaseOpps(soRes.data.opportunities || []);
      } catch (e) {
        console.log('No showcase opportunities found');
      }
      
      // Fetch showcase events
      if (response.data.id) {
        try {
          const showcaseRes = await axios.get(`${API_URL}/api/profiles/${response.data.id}/showcase`);
          setShowcaseEvents(showcaseRes.data.events || []);
          setShowcaseEnabled(showcaseRes.data.showcase_enabled !== false);
          setShowcaseTitle(showcaseRes.data.showcase_title || 'BrandStage Showcase');
        } catch (e) {
          console.log('No showcase events found');
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Profile not found');
      } else {
        setError('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const shareUrl = window.location.href;
  const shareText = profile ? `Check out ${profile.company_name} on BrandStage` : '';
  
  // Analytics tracking helper
  const trackEvent = (eventType) => {
    if (profile?.id) {
      axios.post(`${API_URL}/api/profiles/${profile.id}/track?event_type=${eventType}`).catch(() => {});
    }
  };
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      trackEvent('share_click');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  const handleWhatsAppClick = () => {
    trackEvent('whatsapp_click');
    trackEvent('contact_click');
  };
  
  const handleEmailClick = () => {
    trackEvent('email_click');
    trackEvent('contact_click');
  };
  
  const handleVideoPlay = () => {
    trackEvent('video_play');
  };
  
  const downloadQRCode = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Add padding for the QR code
      const padding = 40;
      canvas.width = img.width + padding * 2;
      canvas.height = img.height + padding * 2 + 60; // Extra space for text
      
      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code
      ctx.drawImage(img, padding, padding);
      
      // Add company name text
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(profile?.company_name || 'BrandStage Profile', canvas.width / 2, canvas.height - 35);
      
      // Add URL text
      ctx.font = '12px Arial';
      ctx.fillStyle = '#666666';
      ctx.fillText(shareUrl.replace('https://', ''), canvas.width / 2, canvas.height - 15);
      
      // Download
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `${profile?.slug || 'profile'}-qr-code.png`;
      downloadLink.href = pngUrl;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    trackEvent('qr_download');
  };
  
  
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
  
  // Get category config
  const categoryConfig = CATEGORY_CONFIG[profile?.profile_category] || CATEGORY_CONFIG.general;
  
  // Calculate KPIs
  const yearsInBusiness = profile?.year_established 
    ? new Date().getFullYear() - parseInt(profile.year_established)
    : null;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Profile Not Found</h2>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link
            to="/"
            className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold inline-block"
          >
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }
  
  // Filter visible testimonials
  const visibleTestimonials = testimonials.filter(t => t.status === 'approved' && t.is_visible);
  
  return (
    <div className="min-h-screen bg-[#09090B] pb-16 md:pb-14">
      {profile?.is_draft && profile?.subscription_status !== 'active' && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-2xl bg-amber-500/15 border border-amber-500/40 backdrop-blur-xl rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="draft-banner">
          <p className="text-amber-200 text-sm"><strong>Private draft.</strong> Only you can see this profile. Complete payment to publish it.</p>
          <Link to={`/pricing?plan=${profile.selected_plan?.product_type || 'basic_1y'}`} className="bg-[#C41E3A] hover:bg-[#A01830] text-white text-sm font-semibold px-4 py-2 rounded-full" data-testid="draft-pay-btn">Pay & publish</Link>
        </div>
      )}
      {/* Floating Header */}
      <header className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-5xl">
        <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-full px-4 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img src={LOGO_URL} alt="BrandStage" className="h-7 w-auto rounded" />
          </Link>
          
          <div className="flex items-center gap-2">
            {/* Language Selector - Only show if multi_language_enabled */}
            {profile?.multi_language_enabled && (
              <div className="relative">
                <button
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  className="flex items-center gap-1 px-2 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm transition-colors"
                  data-testid="language-selector-btn"
                  disabled={translating}
                >
                  {translating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Globe className="w-4 h-4" />
                      <span className="hidden sm:inline">{LANGUAGES.find(l => l.code === selectedLanguage)?.flag}</span>
                    </>
                  )}
                </button>
                
                {showLanguageMenu && (
                  <div className="absolute right-0 top-full mt-2 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl py-2 min-w-[160px] z-50">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => translateProfile(lang.code)}
                        className={`w-full px-4 py-2 text-left text-sm flex items-center gap-3 hover:bg-zinc-800 transition-colors ${
                          selectedLanguage === lang.code ? 'text-[#C41E3A] bg-zinc-800/50' : 'text-zinc-300'
                        }`}
                        data-testid={`lang-option-${lang.code}`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        <span>{lang.native}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <button
              onClick={copyLink}
              className={`p-2 rounded-full transition-colors ${copied ? 'bg-green-600 text-white' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'}`}
              data-testid="copy-link-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full bg-green-600 hover:bg-green-500 text-white transition-colors"
              data-testid="whatsapp-share-btn"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full bg-[#0077B5] hover:bg-[#0088CC] text-white transition-colors"
              data-testid="linkedin-share-btn"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            {canEdit && (
              <button
                onClick={() => navigate(`/profile/edit/${profile.id}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C41E3A] hover:bg-[#A01830] text-white text-sm font-medium transition-colors"
                data-testid="edit-profile-btn"
              >
                <Edit className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
          </div>
        </div>
      </header>
      
      <main className="max-w-5xl mx-auto px-4 pt-24 pb-12">
        {/* BENTO GRID - Main Hero Section */}
        <div className="grid grid-cols-12 gap-3 md:gap-4 mb-6" data-testid="bento-hero-grid">
          {/* Company Info Card - Expands when no hero video */}
          <div className={`col-span-12 ${profile.hero_video?.url ? 'md:col-span-5' : 'md:col-span-12'} bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 md:p-6`}>
            {/* Category Badge - Only show if profile has a category set */}
            {profile.profile_category && (
              <div className="mb-4">
                <CategoryBadge category={profile.profile_category} customCategory={profile.custom_category} />
              </div>
            )}
            
            {/* Logo & Name */}
            <div className={`flex items-start gap-4 mb-4 ${!profile.hero_video?.url ? 'md:flex-row' : ''}`}>
              {profile.logo_url ? (
                <img 
                  src={profile.logo_url} 
                  alt={profile.company_name}
                  className="w-16 h-16 md:w-20 md:h-20 object-contain rounded-xl bg-white p-1.5"
                  data-testid="company-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<div class="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#C41E3A] to-[#8a1428] rounded-xl flex items-center justify-center"><span class="text-2xl font-bold text-white">${profile.company_name?.charAt(0) || 'C'}</span></div>`;
                  }}
                />
              ) : (
                <div className="w-16 h-16 md:w-20 md:h-20 bg-zinc-800 rounded-xl flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-zinc-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-white leading-tight" data-testid="company-name">
                  {profile.company_name}
                </h1>
                {profile.tagline && (
                  <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{t('tagline', profile.tagline)}</p>
                )}
              </div>
            </div>
            
            {/* Quick Info */}
            <div className="flex flex-wrap gap-3 text-xs text-zinc-500">
              {/* Private Profile Badge with ribbon */}
              <span className="relative inline-flex items-center gap-1.5 bg-[#C41E3A]/10 text-[#C41E3A] border border-[#C41E3A]/25 px-3 py-1 rounded-full text-xs font-medium" data-testid="private-profile-badge">
                <Shield className="w-3.5 h-3.5" />
                Private Profile &middot; Shared with you
                <span className="absolute -top-px -right-px w-2 h-2 bg-[#C41E3A] rounded-full animate-pulse" />
              </span>
              {profile.headquarters && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {profile.headquarters}
                </span>
              )}
              {profile.year_established && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Since {profile.year_established}
                </span>
              )}
            </div>
            
            {/* Certifications */}
            {profile.certifications && profile.certifications.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {profile.certifications.slice(0, 3).map((cert, idx) => (
                  <span key={idx} className="bg-[#1E3A5F]/20 text-[#6B9FD4] px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
                    <Award className="w-3 h-3" />
                    {cert}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* Hero Video Card - Adapts when no video */}
          {profile.hero_video?.url ? (
            <div className="col-span-12 md:col-span-7">
              <BentoVideoCard video={profile.hero_video} size="hero" onPlay={() => trackVideoPlay(slug, 'hero')} />
            </div>
          ) : (
            <div className="col-span-12 md:col-span-7 hidden md:block" /> 
          )}
          
          {/* KPI Stats Row - Smart responsive */}
          <KPIRow kpis={[
            { value: yearsInBusiness ? `${yearsInBusiness}+` : null, label: "Years", icon: Calendar },
            { value: profile.clients_count, label: "Clients", icon: Users },
            { value: profile.products_count, label: "Products", icon: Package },
            { value: profile.countries_count, label: "Countries", icon: Globe }
          ]} />
        </div>
        
        {/* BENTO GRID - About Section */}
        <div className="grid grid-cols-12 gap-3 md:gap-4 mb-6" data-testid="bento-about-grid">
          {/* AI Content Indicator */}
          {profile.profile_sections?.who_we_are && (
            <div className="col-span-12 flex justify-end">
              <span className="text-xs text-zinc-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                AI-assisted content
              </span>
            </div>
          )}
          
          {/* Who We Are - Large Card */}
          {profile.profile_sections?.who_we_are && (
            <div className="col-span-12 md:col-span-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 md:p-6">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
                <Building2 className="w-4 h-4 text-[#C41E3A]" />
                Who We Are
              </h2>
              <p className="text-zinc-300 leading-relaxed">{tSection('who_we_are', profile.profile_sections.who_we_are)}</p>
            </div>
          )}
          
          {/* Our Presence - Small Card */}
          {profile.profile_sections?.our_presence && (
            <div className="col-span-12 md:col-span-4 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
                <Globe className="w-4 h-4 text-[#C41E3A]" />
                Our Presence
              </h2>
              <p className="text-zinc-300 text-sm leading-relaxed">{tSection('our_presence', profile.profile_sections.our_presence)}</p>
            </div>
          )}
          
          {/* Looking For - Small Card */}
          {profile.profile_sections?.what_we_seek && (
            <div className="col-span-12 md:col-span-4 bg-[#1E3A5F]/10 border border-[#1E3A5F]/30 rounded-2xl p-5">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
                <Handshake className="w-4 h-4 text-[#C41E3A]" />
                Looking For
              </h2>
              <p className="text-zinc-300 text-sm leading-relaxed">{tSection('what_we_seek', profile.profile_sections.what_we_seek)}</p>
            </div>
          )}
          
          {/* Why Partner - Large Card */}
          {profile.profile_sections?.why_partner && (
            <div className="col-span-12 md:col-span-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 md:p-6">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
                <Star className="w-4 h-4 text-[#C41E3A]" />
                Why Partner With Us
              </h2>
              <p className="text-zinc-300 leading-relaxed">{tSection('why_partner', profile.profile_sections.why_partner)}</p>
            </div>
          )}
          
          {/* Fallback for legacy profiles without sections OR custom text mode */}
          {(!profile.profile_sections || (
            !profile.profile_sections.who_we_are && 
            !profile.profile_sections.our_presence && 
            !profile.profile_sections.what_we_seek && 
            !profile.profile_sections.why_partner
          )) && profile.profile_text && (
            <div className="col-span-12 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 md:p-6">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
                <Building2 className="w-4 h-4 text-[#C41E3A]" />
                About {profile.company_name}
              </h2>
              <div className="text-zinc-300 leading-relaxed whitespace-pre-line">{t('profile_text', profile.profile_text)}</div>
            </div>
          )}
        </div>
        
        {/* FEATURED VIDEOS SECTION - Moved above Showcase for better visual hierarchy */}
        {profile.additional_videos && profile.additional_videos.filter(v => v.url).length > 0 && (
          <div className="mb-6" data-testid="videos-section">
            <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
              <Video className="w-4 h-4 text-[#C41E3A]" />
              Featured Videos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profile.additional_videos.filter(v => v.url).map((video, idx) => (
                <BentoVideoCard key={idx} video={video} size="small" onPlay={() => trackVideoPlay(slug, video.url)} />
              ))}
            </div>
          </div>
        )}
        
        {/* BRANDSTAGE SHOWCASE SECTION */}
        {showcaseEnabled && showcaseEvents.length > 0 && (
          <div className="mb-6" data-testid="showcase-section">
            <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
              <Tv className="w-4 h-4 text-[#C41E3A]" />
              {showcaseTitle}
            </h2>
            <div className={`grid gap-4 ${showcaseEvents.length === 1 ? 'grid-cols-1 max-w-lg' : 'grid-cols-1 md:grid-cols-2'}`}>
              {showcaseEvents.slice(0, 2).map((event) => {
                const isGallery = event.event_format === 'gallery';
                const heroVideo = event.videos?.find(v => v.youtube_id === event.hero_video_id) || event.videos?.[0];
                const youtubeId = heroVideo?.youtube_id;
                const coverImg = event.cover_image || event.gallery_images?.[0];
                const eventDate = new Date(event.event_date);
                
                return (
                  <Link
                    key={event.id}
                    to={`/p/${slug}/showcase/${event.id}`}
                    onClick={() => isGallery ? trackPhotoView(slug) : trackVideoPlay(slug, event.id)}
                    className="group bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-[#C41E3A]/50 transition-all duration-300"
                    data-testid={`showcase-event-${event.id}`}
                  >
                    {/* Thumbnail - handles both video and gallery formats */}
                    <div className="aspect-video relative bg-zinc-800">
                      {isGallery ? (
                        coverImg ? (
                          <img 
                            src={coverImg}
                            alt={event.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-zinc-900">
                            <FolderOpen className="w-12 h-12 text-purple-400" />
                          </div>
                        )
                      ) : youtubeId ? (
                        <img 
                          src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-12 h-12 text-zinc-600" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-[#C41E3A]/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                          {isGallery ? (
                            <FolderOpen className="w-6 h-6 text-white" />
                          ) : (
                            <Play className="w-6 h-6 text-white fill-white ml-0.5" />
                          )}
                        </div>
                      </div>
                      {/* Count badge */}
                      {isGallery ? (
                        event.gallery_images?.length > 0 && (
                          <div className="absolute top-2 right-2 px-2 py-1 bg-black/70 rounded-md text-xs text-white flex items-center gap-1">
                            <FolderOpen className="w-3 h-3" />
                            {event.gallery_images.length} photos
                          </div>
                        )
                      ) : (
                        event.videos?.length > 0 && (
                          <div className="absolute top-2 right-2 px-2 py-1 bg-black/70 rounded-md text-xs text-white flex items-center gap-1">
                            <Video className="w-3 h-3" />
                            {event.videos.length} {event.videos.length === 1 ? 'video' : 'videos'}
                          </div>
                        )
                      )}
                    </div>
                    {/* Content */}
                    <div className="p-4">
                      <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2">
                        <Calendar className="w-3 h-3" />
                        <span>{eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        {event.location && (
                          <>
                            <span>•</span>
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{event.location}</span>
                          </>
                        )}
                      </div>
                      <h3 className="font-semibold text-white group-hover:text-[#C41E3A] transition-colors line-clamp-2">
                        {event.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-2 text-zinc-500 text-xs">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {event.views || 0}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        
        {/* TESTIMONIALS SECTION */}
        {visibleTestimonials.length > 0 && (
          <div className="mb-6" data-testid="testimonials-section">
            <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
              <MessageCircle className="w-4 h-4 text-[#C41E3A]" />
              Testimonials
            </h2>
            <div className={`grid gap-4 ${
              visibleTestimonials.length === 1 
                ? 'grid-cols-1 max-w-md' 
                : visibleTestimonials.length === 2 
                ? 'grid-cols-1 md:grid-cols-2 max-w-2xl'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {visibleTestimonials.map((testimonial) => (
                <TestimonialCard 
                  key={testimonial.id} 
                  testimonial={testimonial}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* ANIMATED NOTIFICATION — Active Opportunities */}
        {showcaseOpps.length > 0 && (
          <div className="mb-6" data-testid="opportunity-notification-banner">
            <Link
              to={`/p/${slug}/opportunity/${showcaseOpps[0].slug}`}
              className="block bg-gradient-to-r from-[#C41E3A]/10 via-[#C41E3A]/5 to-[#C41E3A]/10 border border-[#C41E3A]/30 rounded-2xl px-5 py-3.5 hover:border-[#C41E3A]/50 transition-all group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-[#C41E3A]/5 animate-pulse" />
              <div className="relative flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#C41E3A]/20 flex items-center justify-center shrink-0">
                  <Megaphone className="w-4 h-4 text-[#C41E3A] animate-bounce" style={{ animationDuration: '2s' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#C41E3A] text-xs font-bold uppercase tracking-wider">New Opportunity Posted</p>
                  <p className="text-white text-sm font-semibold truncate group-hover:text-[#C41E3A] transition-colors">{showcaseOpps[0].title}</p>
                </div>
                <span className="text-[#C41E3A] text-xs font-medium hidden sm:flex items-center gap-1">
                  View Details <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          </div>
        )}

        {/* SHOWCASE OPPORTUNITY PAGES */}
        {showcaseOpps.length > 0 && (
          <div className="mb-6" data-testid="showcase-opportunities-section">
            <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
              <Megaphone className="w-4 h-4 text-[#C41E3A]" />
              Opportunities
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {showcaseOpps.map((opp) => (
                <Link
                  key={opp.id}
                  to={`/p/${slug}/opportunity/${opp.slug}`}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 hover:border-[#C41E3A]/50 hover:bg-zinc-800/50 transition-all group"
                  data-testid={`showcase-opp-${opp.slug}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-semibold text-white group-hover:text-[#C41E3A] transition-colors line-clamp-2 text-sm">
                      {opp.title}
                    </h3>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#C41E3A]/10 text-[#C41E3A] whitespace-nowrap font-medium">
                      {(opp.category || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                  {opp.overview && (
                    <p className="text-zinc-400 text-xs line-clamp-2 mb-3">{opp.overview}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                    {opp.location && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{opp.location}</span>
                    )}
                    {opp.key_dates?.deadline && (
                      <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />Deadline: {new Date(opp.key_dates.deadline).toLocaleDateString()}</span>
                    )}
                    {opp.documents?.length > 0 && (
                      <span className="flex items-center gap-1"><FolderOpen className="w-3 h-3" />{opp.documents.length} doc{opp.documents.length > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-end mt-3 pt-3 border-t border-zinc-800">
                    <span className="text-[#C41E3A] text-xs flex items-center gap-1 group-hover:gap-2 transition-all font-medium">
                      View Details <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* OPPORTUNITIES SECTION */}
        {opportunities.length > 0 && (
          <div className="mb-6" data-testid="opportunities-section">
            <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
              <Link2 className="w-4 h-4 text-[#C41E3A]" />
              Business Opportunities
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {opportunities.map((opp, idx) => (
                <Link 
                  key={idx}
                  to={`/connect?opp=${opp.id}`}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-[#C41E3A]/50 hover:bg-zinc-800/50 transition-all group"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-semibold text-white group-hover:text-[#C41E3A] transition-colors line-clamp-2">
                      {opp.title}
                    </h3>
                    <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-300 whitespace-nowrap">
                      {opp.category.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-zinc-400">
                    {opp.sector && <span>{opp.sector}</span>}
                    {opp.geography && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {opp.geography}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
                    <span className="text-xs text-zinc-500">
                      {new Date(opp.created_at).toLocaleDateString()}
                    </span>
                    <span className="text-[#C41E3A] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      View <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Link 
                to="/connect" 
                className="text-sm text-[#C41E3A] hover:underline inline-flex items-center gap-1"
              >
                View all opportunities on Connect <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}
        
        {/* LEADERSHIP + CONTACT - Side by Side Bento */}
        <div className="grid grid-cols-12 gap-3 md:gap-4" data-testid="bento-contact-grid">
          {/* Leadership Card - Expanded to show full details */}
          {profile.key_contact && (
            <div className="col-span-12 md:col-span-7 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 md:p-6">
              <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
                <User className="w-4 h-4 text-[#C41E3A]" />
                Leadership
              </h2>
              <div className="flex gap-4">
                {profile.key_contact.photo_url ? (
                  <img 
                    src={profile.key_contact.photo_url}
                    alt={profile.contact_name}
                    className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#C41E3A] to-[#8a1428] flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-bold text-white">
                      {profile.contact_name?.charAt(0) || 'L'}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-lg">{profile.contact_name}</p>
                  <p className="text-[#C41E3A] text-sm font-medium">{profile.contact_designation}</p>
                  {profile.key_contact.years_experience && (
                    <p className="text-zinc-500 text-xs mt-1">{profile.key_contact.years_experience}+ years of experience</p>
                  )}
                </div>
              </div>
              
              {/* Full Bio Text */}
              {profile.key_contact.bio_text && (
                <p className="text-zinc-300 text-sm mt-4 leading-relaxed">{tKeyContact('bio_text', profile.key_contact.bio_text)}</p>
              )}
              
              {/* All Expertise Areas */}
              {profile.key_contact.expertise_areas && profile.key_contact.expertise_areas.length > 0 && (
                <div className="mt-4">
                  <p className="text-zinc-500 text-xs uppercase tracking-wide mb-2">Areas of Expertise</p>
                  <div className="flex flex-wrap gap-1.5">
                    {profile.key_contact.expertise_areas.map((exp, idx) => (
                      <span key={idx} className="bg-[#C41E3A]/10 text-[#C41E3A] px-2 py-0.5 rounded text-xs">
                        {exp}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Notable Achievements */}
              {profile.key_contact.notable_achievements && (
                <div className="mt-4">
                  <p className="text-zinc-500 text-xs uppercase tracking-wide mb-2">Notable Achievements</p>
                  <p className="text-zinc-400 text-sm leading-relaxed">{tKeyContact('notable_achievements', profile.key_contact.notable_achievements)}</p>
                </div>
              )}
              
              {/* LinkedIn Profile */}
              {profile.key_contact.linkedin_url && (
                <a 
                  href={profile.key_contact.linkedin_url.startsWith('http') ? profile.key_contact.linkedin_url : `https://${profile.key_contact.linkedin_url}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 text-[#0077B5] hover:text-[#0088CC] text-sm transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                  View LinkedIn Profile
                </a>
              )}
              
              {/* Key Contact Videos */}
              {profile.key_contact.videos && profile.key_contact.videos.length > 0 && (
                <div className="mt-5 pt-5 border-t border-zinc-700">
                  <p className="text-zinc-500 text-xs uppercase tracking-wide mb-3">Featured Videos</p>
                  <div className="grid gap-3">
                    {profile.key_contact.videos.map((video, idx) => (
                      <div key={idx} className="relative rounded-xl overflow-hidden bg-zinc-800">
                        {video.url && (
                          video.url.includes('youtube.com') || video.url.includes('youtu.be') ? (
                            <div className="relative">
                              <div 
                                className="aspect-video bg-zinc-900 cursor-pointer group relative"
                                onClick={() => {
                                  const videoId = video.url.includes('youtu.be') 
                                    ? video.url.split('youtu.be/')[1]?.split('?')[0]
                                    : video.url.split('v=')[1]?.split('&')[0];
                                  if (videoId) {
                                    window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
                                  }
                                }}
                              >
                                <img 
                                  src={(() => {
                                    const videoId = video.url.includes('youtu.be') 
                                      ? video.url.split('youtu.be/')[1]?.split('?')[0]
                                      : video.url.split('v=')[1]?.split('&')[0];
                                    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
                                  })()}
                                  alt={video.title || 'Video thumbnail'}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    const videoId = video.url.includes('youtu.be') 
                                      ? video.url.split('youtu.be/')[1]?.split('?')[0]
                                      : video.url.split('v=')[1]?.split('&')[0];
                                    e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                                  }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                                  <div className="w-14 h-14 rounded-full bg-[#C41E3A] flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <Play className="w-6 h-6 text-white ml-1" fill="white" />
                                  </div>
                                </div>
                              </div>
                              {video.title && (
                                <p className="text-zinc-300 text-sm mt-2 px-1">{video.title}</p>
                              )}
                            </div>
                          ) : (
                            <video 
                              src={video.url} 
                              controls 
                              className="w-full aspect-video"
                              poster={video.thumbnail_url}
                            />
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Contact Card */}
          <div className={`col-span-12 ${profile.key_contact ? 'md:col-span-5' : 'md:col-span-12'} bg-gradient-to-br from-[#C41E3A]/20 to-zinc-900 border border-[#C41E3A]/30 rounded-2xl p-5 md:p-6`}>
            <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
              <Mail className="w-4 h-4 text-[#C41E3A]" />
              Get in Touch
            </h2>
            
            <div className="space-y-3 mb-5">
              {profile.contact_email && (
                <a href={`mailto:${profile.contact_email}`} className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm transition-colors">
                  <Mail className="w-4 h-4 text-zinc-500" />
                  {profile.contact_email}
                </a>
              )}
              {profile.contact_phone && (
                <a href={`tel:${profile.contact_phone}`} className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm transition-colors">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  {profile.contact_phone}
                </a>
              )}
              {profile.website && (
                <a href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm transition-colors">
                  <LinkIcon className="w-4 h-4 text-zinc-500" />
                  {profile.website.replace(/^https?:\/\//, '')}
                </a>
              )}
              
              {profile.resources_enabled && profile.resources_link && (
                <a 
                  href={profile.resources_link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm transition-colors"
                  data-testid="resources-link"
                >
                  <FolderOpen className="w-4 h-4 text-zinc-500" />
                  {profile.resources_label || 'Company Resources'}
                </a>
              )}

              {/* Social Media Links — polished icon pills */}
              {(profile.social_urls?.linkedin || profile.social_urls?.twitter || profile.social_urls?.instagram) && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {profile.social_urls?.linkedin && (
                    <a href={profile.social_urls.linkedin.startsWith('http') ? profile.social_urls.linkedin : `https://${profile.social_urls.linkedin}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-[#0A66C2]/20 border border-zinc-700 hover:border-[#0A66C2]/40 text-zinc-300 hover:text-[#0A66C2] px-3 py-1.5 rounded-full text-xs font-medium transition-colors" data-testid="social-linkedin">
                      <Linkedin className="w-3.5 h-3.5" />
                      LinkedIn
                    </a>
                  )}
                  {profile.social_urls?.twitter && (
                    <a href={profile.social_urls.twitter.startsWith('http') ? profile.social_urls.twitter : `https://${profile.social_urls.twitter}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white px-3 py-1.5 rounded-full text-xs font-medium transition-colors" data-testid="social-twitter">
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      X
                    </a>
                  )}
                  {profile.social_urls?.instagram && (
                    <a href={profile.social_urls.instagram.startsWith('http') ? profile.social_urls.instagram : `https://${profile.social_urls.instagram}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-[#E4405F]/20 border border-zinc-700 hover:border-[#E4405F]/40 text-zinc-300 hover:text-[#E4405F] px-3 py-1.5 rounded-full text-xs font-medium transition-colors" data-testid="social-instagram">
                      <Instagram className="w-3.5 h-3.5" />
                      Instagram
                    </a>
                  )}
                </div>
              )}
            </div>
            
            {/* CTA Buttons */}
            <div className="space-y-2">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWhatsAppClick}
                className="w-full bg-green-600 hover:bg-green-500 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors text-sm"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                Chat on WhatsApp
              </a>
              <a
                href={emailUrl}
                onClick={handleEmailClick}
                className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors text-sm"
                data-testid="contact-email-btn"
              >
                <Mail className="w-4 h-4" />
                {categoryConfig.cta}
              </a>
            </div>
          </div>
        </div>
        
        {/* Call to Action Banner */}
        {profile.profile_sections?.call_to_action && (
          <div className="mt-6 bg-gradient-to-r from-[#1E3A5F]/30 to-[#1E3A5F]/10 border border-[#1E3A5F]/30 rounded-2xl p-5 text-center">
            <p className="text-white font-medium">{profile.profile_sections.call_to_action}</p>
          </div>
        )}
        
        {/* QR Code Section */}
        <div className="mt-6 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-white font-bold text-lg mb-2 flex items-center gap-2 justify-center md:justify-start">
                <QrCode className="w-5 h-5 text-[#C41E3A]" />
                Share This Profile
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                Scan the QR code or download it to print on business cards, brochures, or marketing materials.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setShowQRModal(true)}
                  className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  data-testid="view-qr-btn"
                >
                  <QrCode className="w-4 h-4" />
                  View QR Code
                </button>
              </div>
            </div>
            
            {/* Small QR Preview - with ref for signature download */}
            <div 
              ref={qrRef}
              className="bg-white p-3 rounded-xl cursor-pointer hover:scale-105 transition-transform"
              onClick={() => setShowQRModal(true)}
              data-testid="qr-preview"
            >
              <QRCode 
                value={shareUrl} 
                size={100}
                level="M"
              />
            </div>
          </div>
        </div>
        
        {/* Translation Notice - Subtle footer when viewing translated content */}
        {selectedLanguage !== 'en' && (
          <div className="mt-8 pt-6 border-t border-zinc-800/50">
            <p className="text-xs text-zinc-500 text-center">
              This content has been translated using AI-powered services. 
              For corrections, contact{' '}
              <a 
                href="mailto:operations@brandstage-media.com?subject=Translation Review Request" 
                className="text-zinc-400 hover:text-[#C41E3A] underline"
              >
                operations@brandstage-media.com
              </a>
            </p>
          </div>
        )}
      </main>
      
      {/* QR Code Modal */}
      {showQRModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowQRModal(false)}
        >
          <div 
            className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-lg">QR Code</h3>
              <button
                onClick={() => setShowQRModal(false)}
                className="p-1 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            {/* QR Code */}
            <div 
              className="bg-white p-6 rounded-xl flex items-center justify-center mb-4"
            >
              <QRCode 
                value={shareUrl} 
                size={200}
                level="H"
              />
            </div>
            
            {/* Company Name */}
            <p className="text-white font-semibold text-center mb-1">{profile.company_name}</p>
            <p className="text-zinc-500 text-xs text-center mb-4 break-all">{shareUrl}</p>
            
            {/* Download Buttons */}
            <div className="space-y-2">
              <button
                onClick={downloadQRCode}
                className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                data-testid="download-qr-btn"
              >
                <Download className="w-4 h-4" />
                Download QR Code
              </button>
            </div>
            
            <p className="text-zinc-500 text-xs text-center mt-3">
              QR for print materials
            </p>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="border-t border-zinc-800 py-6">
        <div className="max-w-5xl mx-auto px-4">
          {/* AI Content Disclaimer */}
          <div className="mb-4 p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg">
            <p className="text-zinc-500 text-xs leading-relaxed">
              <span className="text-zinc-400 font-medium">AI Disclaimer:</span> Some content on this profile may be AI-assisted. 
              While we strive for accuracy, BrandStage does not guarantee the completeness or accuracy of AI-generated information. 
              Please verify critical details directly with the company or make changes manually if required.
            </p>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-zinc-500 text-xs">
              Profile by {profile.company_name}. Information not verified by BrandStage.
            </p>
            <Link to="/" className="text-zinc-400 hover:text-white text-xs flex items-center gap-2">
              Powered by <img src={LOGO_URL} alt="BrandStage" className="h-5 w-auto rounded" />
            </Link>
          </div>
        </div>
      </footer>
      
      {/* Mobile Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur-xl border-t border-zinc-800 p-3 md:hidden z-50">
        <div className="flex items-center gap-2">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleWhatsAppClick}
            className="flex-1 bg-green-600 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
          <a
            href={emailUrl}
            onClick={handleEmailClick}
            className="flex-1 bg-[#C41E3A] text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
          >
            <Mail className="w-4 h-4" />
            Email
          </a>
          <button
            onClick={copyLink}
            className={`p-2.5 rounded-lg ${copied ? 'bg-green-600' : 'bg-zinc-800'} text-white`}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>
      
      {/* Video Modal */}
      {selectedVideo && (
        <VideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />
      )}
      
      {/* BrandStage Promo Footer Bar */}
      {showPromoBar && (
        <div 
          className={`fixed left-0 right-0 transition-transform duration-300 ${
            promoBarVisible ? 'translate-y-0' : 'translate-y-full'
          } bottom-[60px] md:bottom-0 z-30 md:z-40`}
          data-testid="brandstage-promo-bar"
        >
          <div className="bg-zinc-900/95 backdrop-blur-sm border-t border-zinc-800">
            <div className="max-w-6xl mx-auto px-4 py-2 md:py-3">
              {/* Desktop Layout */}
              <div className="hidden md:flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-zinc-400 text-sm">
                  <span className="text-white font-medium">Powered by BrandStage</span>
                  <span className="text-zinc-600">—</span>
                  <span>Your living digital footprint</span>
                </div>
                <div className="flex items-center gap-4 text-zinc-500 text-xs">
                  <span>Profile</span>
                  <span className="text-zinc-700">•</span>
                  <span>Video</span>
                  <span className="text-zinc-700">•</span>
                  <span>Testimonials</span>
                  <span className="text-zinc-700">•</span>
                  <span>Score</span>
                  <span className="text-zinc-700">•</span>
                  <span>Connect</span>
                  <span className="text-zinc-700">•</span>
                  <span>Showcase</span>
                </div>
                <div className="flex items-center gap-3">
                  <Link 
                    to="/" 
                    className="text-[#C41E3A] hover:text-[#E52545] text-sm font-medium flex items-center gap-1 transition-colors"
                  >
                    Create your profile
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <button 
                    onClick={dismissPromoBar}
                    className="text-zinc-600 hover:text-zinc-400 p-1 transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Mobile Layout */}
              <div className="md:hidden">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-white font-medium">Powered by BrandStage</span>
                    </div>
                    <p className="text-zinc-500 text-xs mt-0.5 truncate">
                      Profile • Video • Testimonials • Score • Connect
                    </p>
                  </div>
                  <Link 
                    to="/" 
                    className="text-[#C41E3A] text-xs font-medium flex items-center gap-1 whitespace-nowrap"
                  >
                    Create profile
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                  <button 
                    onClick={dismissPromoBar}
                    className="text-zinc-600 hover:text-zinc-400 p-1"
                    aria-label="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
